import React, { useState } from "react";
import { Box, Paper, TextField, Button, Stack, Typography, Chip, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, MenuItem, List, ListItemButton, ListItemText } from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { kbService } from "../../api/services/kbService";
import { PageHeader, StatusChip, ConfirmDialog } from "../../components/Common";
import DataTable from "../../components/DataTable";

export default function KbPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [categoryId, setCategoryId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [deleteCat, setDeleteCat] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", is_faq: false, category_id: "" });

  const { data: categories } = useQuery({ queryKey: ["kb-categories"], queryFn: kbService.listCategories });
  const { data: articles, isLoading } = useQuery({
    queryKey: ["kb-articles", categoryId],
    queryFn: () => kbService.searchArticles(categoryId ? { category_id: categoryId } : {}),
  });

  const createCategoryMutation = useMutation({
    mutationFn: () => kbService.createCategory({ name: newCategory }),
    onSuccess: () => {
      setNewCategory("");
      queryClient.invalidateQueries({ queryKey: ["kb-categories"] });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id) => kbService.deleteCategory(id),
    onSuccess: () => {
      enqueueSnackbar("Category deleted", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["kb-categories"] });
      setDeleteCat(null);
    },
    onError: (err) => {
      enqueueSnackbar(err.response?.data?.detail || "Could not delete category", { variant: "error" });
      setDeleteCat(null);
    },
  });

  const createArticleMutation = useMutation({
    mutationFn: () => kbService.createArticle({ ...form, category_id: form.category_id || null }),
    onSuccess: (article) => {
      setCreateOpen(false);
      setForm({ title: "", content: "", is_faq: false, category_id: "" });
      enqueueSnackbar("Article created as draft", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["kb-articles"] });
      navigate(`/kb/${article.id}`);
    },
  });

  const columns = [
    { key: "title", label: "Title", render: (a) => <strong>{a.title}</strong> },
    { key: "status", label: "Status", render: (a) => <StatusChip value={a.status} /> },
    { key: "is_faq", label: "FAQ", render: (a) => (a.is_faq ? <Chip size="small" label="FAQ" /> : "-") },
    { key: "view_count", label: "Views" },
    { key: "updated_at", label: "Updated", render: (a) => new Date(a.updated_at).toLocaleDateString() },
  ];

  return (
    <Box>
      <PageHeader
        title="Knowledge Base"
        subtitle="Articles, FAQ, and category management"
        action={<Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>New Article</Button>}
      />

      <Stack direction="row" spacing={3}>
        <Paper variant="outlined" sx={{ width: 240, flexShrink: 0, p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Categories</Typography>
          <List dense>
            <ListItemButton selected={categoryId === null} onClick={() => setCategoryId(null)}>
              <ListItemText primary="All articles" />
            </ListItemButton>
            {(categories || []).map((c) => (
              <ListItemButton key={c.id} selected={categoryId === c.id} onClick={() => setCategoryId(c.id)}
                sx={{ display: "flex", justifyContent: "space-between" }}>
                <ListItemText primary={c.name} />
                <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteCat(c); }}>
                  <Trash2 size={14} />
                </Button>
              </ListItemButton>
            ))}
          </List>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <TextField size="small" placeholder="New category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} fullWidth />
            <Button size="small" variant="outlined" disabled={!newCategory} onClick={() => createCategoryMutation.mutate()}>Add</Button>
          </Stack>
        </Paper>

        <Box sx={{ flex: 1 }}>
          <DataTable
            columns={columns} rows={articles} loading={isLoading}
            emptyMessage="No articles yet" onRowClick={(a) => navigate(`/kb/${a.id}`)}
          />
        </Box>
      </Stack>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Article</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} fullWidth required />
            <TextField select label="Category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
              <MenuItem value="">None</MenuItem>
              {(categories || []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField label="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} multiline minRows={4} fullWidth required />
            <FormControlLabel control={<Checkbox checked={form.is_faq} onChange={(e) => setForm({ ...form, is_faq: e.target.checked })} />} label="Mark as FAQ" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={!form.title || !form.content || createArticleMutation.isPending} onClick={() => createArticleMutation.mutate()}>
            Create Draft
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteCat)}
        title="Delete category?"
        message={`Delete "${deleteCat?.name}"? Categories with articles can't be deleted until those articles are moved or removed.`}
        confirmLabel="Delete" danger
        loading={deleteCategoryMutation.isPending}
        onCancel={() => setDeleteCat(null)}
        onConfirm={() => deleteCategoryMutation.mutate(deleteCat.id)}
      />
    </Box>
  );
}
